import {
  Alert,
  Avatar,
  Box,
  Chip,
  Container,
  Divider,
  Fab,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Fade,
} from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import { useExpensesInRange } from "../../hooks/expense";
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  Add as AddIcon,
  LoginOutlined, 
  InsightsOutlined,
  LocalFireDepartmentOutlined,
  PieChartOutline,
  SavingsOutlined,
  TrendingDown,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { getMonthsLongNames } from "../../utils/DateUtil";
import { useAuth } from "../../auth/AuthContext";
import { LOGIN_ROUTE_PATH } from "../../utils/constants/AppConstants";
import ExpenseTableComponent from "../../components/finance/reports/ExpensesTableComponent";
import { PageContainer } from "../../components/common/StyledComponents";
import SurfaceCard from "../../components/common/ui/SurfaceCard";
import PrimaryButton from "../../components/common/ui/PrimaryButton";
import MonthYearPicker from "../../components/common/MonthYearPicker";
import { getMonthIndex, getFirstDayOfMonth, getLastDayOfMonth } from "../../utils/DateUtil";
import { useCurrencyFormatter } from "../../contexts/CurrencyContext";

const ExpensePage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { isAuthenticated } = useAuth();
  const { formatCurrencyNumber: formatCurrency } = useCurrencyFormatter();
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonthIndex = today.getMonth();
  const monthNames = getMonthsLongNames();

  const [searchParams, setSearchParams] = useSearchParams();
  const urlYear = Number(searchParams.get("year")) || currentYear;
  const urlMonth = searchParams.get("month") || monthNames[currentMonthIndex];

  const [selectedYear, setSelectedYear] = useState(urlYear);
  const [selectedMonth, setSelectedMonth] = useState(urlMonth);
  const [dataKey, setDataKey] = useState(0); // For triggering transitions
  const [quickAddTrigger, setQuickAddTrigger] = useState(0);
  const [showQuickAddFab, setShowQuickAddFab] = useState(true);
  const accent = theme.palette.primary.main;
  const accentSoft = `${theme.palette.primary.main}24`;
  const accentSofter = `${theme.palette.primary.main}14`;

  const updateSearchParams = useCallback((newParams: Record<string, string>, replace = false) => {
    const updatedParams = new URLSearchParams(searchParams.toString());
    Object.entries(newParams).forEach(([key, value]) => {
      if (value) {
        updatedParams.set(key, value);
      } else {
        updatedParams.delete(key);
      }
    });
    setSearchParams(updatedParams, { replace });
  }, [searchParams, setSearchParams]);

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    updateSearchParams({ year: year.toString() });
    setDataKey(prev => prev + 1); // Trigger transition
  };

  const handleMonthChange = (month: string) => {
    setSelectedMonth(month);
    updateSearchParams({ month });
    setDataKey(prev => prev + 1); // Trigger transition
  };

  const startDate = getFirstDayOfMonth(selectedYear, getMonthIndex(selectedMonth));
  const endDate = getLastDayOfMonth(selectedYear, getMonthIndex(selectedMonth));
  
  const { data: expenses = [] } = useExpensesInRange(startDate, endDate);
  
  const handleExpenseChanged = () => {
    setDataKey(prev => prev + 1);
  };

  const handleQuickAddExpense = () => {
    setQuickAddTrigger((prev) => prev + 1);
  };

  const totalSpend = expenses.reduce((sum, expense) => sum + expense.amount, 0);
  const averageSpend = expenses.length > 0 ? totalSpend / expenses.length : 0;
  const highestExpense = expenses.length
    ? expenses.reduce((max, expense) => (expense.amount > max.amount ? expense : max))
    : null;

  const spendingByCategory = expenses.reduce<Record<string, number>>((acc, expense) => {
    acc[expense.category] = (acc[expense.category] || 0) + expense.amount;
    return acc;
  }, {});

  const topCategory = Object.entries(spendingByCategory).sort((a, b) => b[1] - a[1])[0];
  const topCategories = Object.entries(spendingByCategory)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  const selectedMonthIndex = getMonthIndex(selectedMonth);
  const isCurrentPeriod = selectedYear === currentYear && selectedMonthIndex === currentMonthIndex;
  const daysInSelectedMonth = new Date(selectedYear, selectedMonthIndex + 1, 0).getDate();
  const elapsedDays = isCurrentPeriod ? today.getDate() : daysInSelectedMonth;
  const avgDailySpend = elapsedDays > 0 ? totalSpend / elapsedDays : 0;
  const projectedMonthSpend = isCurrentPeriod ? avgDailySpend * daysInSelectedMonth : totalSpend;

  const runRateDelta = projectedMonthSpend - totalSpend;
  const spendPaceMessage =
    expenses.length === 0
      ? "No expenses recorded for this period yet. Start adding transactions to unlock pacing insights."
      : isCurrentPeriod
        ? `Current pace: ${formatCurrency(avgDailySpend)}/day. Projected month total: ${formatCurrency(projectedMonthSpend)}.`
        : `Period complete: ${formatCurrency(totalSpend)} across ${expenses.length} transactions.`;
  const unusualExpenses = expenses
    .filter((expense) => expense.amount >= Math.max(averageSpend * 1.75, 1))
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 3);

  const insights = [
    {
      label: "Total spent",
      value: formatCurrency(totalSpend),
      hint: `${expenses.length} transactions`,
      icon: <TrendingDown sx={{ color: accent }} />,
      accent: accent,
    },
    {
      label: "Average expense",
      value: formatCurrency(averageSpend),
      hint: "Per transaction",
      icon: <SavingsOutlined sx={{ color: accent }} />,
      accent: accent,
    },
    {
      label: "Top category",
      value: topCategory?.[0] ?? "No category yet",
      hint: topCategory ? formatCurrency(topCategory[1]) : "Add transactions to unlock",
      icon: <PieChartOutline sx={{ color: accent }} />,
      accent: accent,
    },
    {
      label: "Largest expense",
      value: highestExpense ? formatCurrency(highestExpense.amount) : "None",
      hint: highestExpense ? highestExpense.category : "No data this month",
      icon: <LocalFireDepartmentOutlined sx={{ color: accent }} />,
      accent: accent,
    },
  ];

  useEffect(() => {
    if (isAuthenticated) {
      const needsInit = !searchParams.get("year") || !searchParams.get("month");
      if (needsInit) {
        updateSearchParams({
          year: selectedYear.toString(),
          month: selectedMonth,
        }, true);
      }
    }
  }, [isAuthenticated, searchParams, selectedMonth, selectedYear, updateSearchParams]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const updateFabVisibility = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const viewportHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const remaining = documentHeight - (scrollTop + viewportHeight);

      const shouldShow = documentHeight <= viewportHeight + 40 || remaining > 220;
      setShowQuickAddFab(shouldShow);
    };

    updateFabVisibility();
    window.addEventListener("scroll", updateFabVisibility, { passive: true });
    window.addEventListener("resize", updateFabVisibility);

    return () => {
      window.removeEventListener("scroll", updateFabVisibility);
      window.removeEventListener("resize", updateFabVisibility);
    };
  }, [isAuthenticated, selectedMonth, selectedYear, dataKey]);

  if (!isAuthenticated) {
    return (
      <PageContainer>
        <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>
          <SurfaceCard
            sx={{
              p: { xs: 3, md: 5 },
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
              borderRadius: "18px",
              border: `1px solid ${accentSoft}`,
              background: `linear-gradient(165deg, ${theme.palette.background.paper}, ${theme.palette.background.default})`,
              "&::after": {
                content: '""',
                position: "absolute",
                inset: "auto -30% -65% auto",
                width: 420,
                height: 420,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${accentSoft} 0%, transparent 60%)`,
              },
            }}
          >
            <Avatar
              sx={{
                width: 64,
                height: 64,
                mx: "auto",
                mb: 2,
                backgroundColor: accentSoft,
                color: theme.palette.primary.light,
              }}
            >
              <TrendingDown />
            </Avatar>
            <Typography
              variant="h3"
              gutterBottom
              sx={{
                fontFamily: '"Space Grotesk", "Manrope", sans-serif',
                fontWeight: 700,
                letterSpacing: "-0.02em",
              }}
            >
              Expense Control Center
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 640, mx: "auto", mb: 3 }}>
              Understand exactly where your money goes with a monthly command center designed for fast decisions and crystal clear spending visibility.
            </Typography>
            <PrimaryButton startIcon={<LoginOutlined />} onClick={() => navigate(LOGIN_ROUTE_PATH)} sx={{ px: 4, py: 1.5 }}>
              Sign In to Open Expenses
            </PrimaryButton>
          </SurfaceCard>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container maxWidth="xl" sx={{ pt: { xs: 2, md: 4 }, pb: { xs: 9, md: 4 } }}>
        <Fade in={true} timeout={400}>
          <Box>
            <SurfaceCard
              sx={{
                mb: 3,
                p: { xs: 2.5, md: 4 },
                borderRadius: "18px",
                border: `1px solid ${accentSoft}`,
                background: `linear-gradient(145deg, ${theme.palette.background.paper}, ${theme.palette.background.default})`,
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: "-180px auto auto -120px",
                  width: 340,
                  height: 340,
                  borderRadius: "50%",
                  background: `radial-gradient(circle, ${accentSoft} 0%, transparent 66%)`,
                },
              }}
            >
              <Grid container spacing={3} alignItems="center" sx={{ position: "relative", zIndex: 1 }}>
                <Grid item xs={12} md={7}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                    <Avatar sx={{ bgcolor: accentSoft, color: theme.palette.primary.light }}>
                      <InsightsOutlined />
                    </Avatar>
                    <Chip
                      label={`${selectedMonth} ${selectedYear}`}
                      sx={{
                        borderRadius: "10px",
                        fontWeight: 700,
                        backgroundColor: accentSofter,
                        color: theme.palette.primary.light,
                      }}
                    />
                  </Stack>
                  <Typography
                    variant="h3"
                    sx={{
                      fontFamily: '"Space Grotesk", "Manrope", sans-serif',
                      fontWeight: 700,
                      letterSpacing: "-0.02em",
                      mb: 1,
                    }}
                  >
                    Monthly Expense Overview
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 700 }}>
                    Understand your monthly spending at a glance, identify where money goes, and act early before costs drift.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={5}>
                  <Alert
                    severity="info"
                    icon={false}
                    sx={{
                      borderRadius: "12px",
                      backgroundColor: accentSofter,
                      border: `1px solid ${accentSoft}`,
                      color: theme.palette.primary.light,
                      py: 0.8,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {spendPaceMessage}
                  </Alert>
                </Grid>
              </Grid>
            </SurfaceCard>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              {insights.map((item, index) => (
                <Grid item key={item.label} xs={12} sm={6} lg={3}>
                  <Fade in={true} timeout={350 + index * 120}>
                    <SurfaceCard
                      sx={{
                        p: 2,
                        borderRadius: "16px",
                        border: `1px solid ${accentSoft}`,
                        background: `linear-gradient(170deg, ${theme.palette.background.paper}, ${theme.palette.background.default})`,
                        transition: "transform 0.2s ease, border-color 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-3px)",
                          borderColor: accent,
                        },
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {item.label}
                        </Typography>
                        {item.icon}
                      </Stack>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          fontFamily: '"Space Grotesk", "Manrope", sans-serif',
                          mb: 0.5,
                        }}
                      >
                        {item.value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.hint}
                      </Typography>
                    </SurfaceCard>
                  </Fade>
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} lg={8}>
                <SurfaceCard sx={{ p: { xs: 2, md: 2.5 }, borderRadius: "18px", overflow: "hidden" }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Analyze Another Period
                  </Typography>
                  <MonthYearPicker
                    selectedMonth={selectedMonth}
                    selectedYear={selectedYear}
                    onMonthChange={handleMonthChange}
                    onYearChange={handleYearChange}
                    colorTheme="primary"
                    transactionCount={expenses.length}
                    isLoading={false}
                  />
                </SurfaceCard>
              </Grid>
              <Grid item xs={12} lg={4}>
                <SurfaceCard sx={{ p: { xs: 2, md: 2.5 }, height: "100%", borderRadius: "18px" }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Focus Recommendation
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Highest pressure category this month:
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      mb: 0.5,
                      color: theme.palette.primary.light,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {topCategory?.[0] ?? "No data"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                    {topCategory ? `${formatCurrency(topCategory[1])} across current period` : "Track at least one expense to generate a recommendation."}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    Largest transaction:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {highestExpense ? `${formatCurrency(highestExpense.amount)} - ${highestExpense.category}` : "No expenses yet"}
                  </Typography>
                </SurfaceCard>
              </Grid>
            </Grid>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <SurfaceCard sx={{ p: { xs: 2, md: 2.5 }, borderRadius: "18px", height: "100%" }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.25 }}>
                    Monthly Run Rate
                  </Typography>
                  <Stack spacing={1.2}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Average daily spend
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {formatCurrency(avgDailySpend)}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {isCurrentPeriod ? "Projected month total" : "Final month total"}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.light }}>
                        {formatCurrency(projectedMonthSpend)}
                      </Typography>
                    </Box>

                    <Chip
                      label={
                        isCurrentPeriod
                          ? `${runRateDelta >= 0 ? "+" : ""}${formatCurrency(runRateDelta)} vs current total`
                          : "Month complete"
                      }
                      sx={{
                        width: "fit-content",
                        borderRadius: "10px",
                        backgroundColor: accentSofter,
                        color: theme.palette.primary.light,
                        fontWeight: 700,
                      }}
                    />
                  </Stack>
                </SurfaceCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <SurfaceCard sx={{ p: { xs: 2, md: 2.5 }, borderRadius: "18px", height: "100%" }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.25 }}>
                    Category Allocation
                  </Typography>

                  {topCategories.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Add expenses to see where most of your spending goes.
                    </Typography>
                  ) : (
                    <Stack spacing={1.1}>
                      {topCategories.map(([category, amount]) => {
                        const ratio = totalSpend > 0 ? (amount / totalSpend) * 100 : 0;

                        return (
                          <Box key={category}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              sx={{ mb: 0.4 }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {category}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatCurrency(amount)} ({ratio.toFixed(0)}%)
                              </Typography>
                            </Stack>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(100, Math.max(0, ratio))}
                              sx={{
                                height: 7,
                                borderRadius: 10,
                                backgroundColor: `${theme.palette.text.primary}14`,
                                "& .MuiLinearProgress-bar": {
                                  borderRadius: 10,
                                  background: `linear-gradient(90deg, ${theme.palette.primary.dark}, ${theme.palette.primary.light})`,
                                },
                              }}
                            />
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </SurfaceCard>
              </Grid>
            </Grid>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <SurfaceCard sx={{ p: { xs: 2, md: 2.5 }, borderRadius: "18px" }}>
                  <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.5 }}>
                    <Avatar sx={{ width: 34, height: 34, bgcolor: accentSofter, color: theme.palette.primary.light }}>
                      <WarningAmberOutlined sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Spend Signals
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Expenses significantly above your average transaction size
                      </Typography>
                    </Box>
                  </Stack>

                  {unusualExpenses.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No unusual transactions in this period. Spending pattern looks stable.
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {unusualExpenses.map((expense) => (
                        <Box
                          key={expense.id}
                          sx={{
                            p: 1.2,
                            borderRadius: "10px",
                            border: `1px solid ${accentSoft}`,
                            backgroundColor: `${theme.palette.background.default}80`,
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                                {expense.description}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {expense.category} • {new Date(expense.date).toLocaleDateString()}
                              </Typography>
                            </Box>
                            <Chip
                              label={formatCurrency(expense.amount)}
                              sx={{
                                borderRadius: "8px",
                                backgroundColor: `${theme.palette.error.main}1f`,
                                color: theme.palette.error.light,
                                fontWeight: 700,
                              }}
                            />
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </SurfaceCard>
              </Grid>
            </Grid>

            <Fade in={true} timeout={600} key={dataKey}>
              <SurfaceCard sx={{ overflow: "hidden", borderRadius: "18px" }}>
                <ExpenseTableComponent
                  year={selectedYear}
                  month={selectedMonth}
                  onExpenseChange={handleExpenseChanged}
                  addEntryTrigger={quickAddTrigger}
                />
              </SurfaceCard>
            </Fade>

            <Fab
              color="primary"
              variant={isMobile ? "circular" : "extended"}
              onClick={handleQuickAddExpense}
              aria-label="Add expense"
              sx={{
                position: "fixed",
                right: { xs: 16, md: 28 },
                bottom: { xs: "calc(82px + env(safe-area-inset-bottom))", md: 28 },
                zIndex: (theme) => theme.zIndex.appBar + 2,
                px: 2,
                borderRadius: 999,
                boxShadow: "0 14px 34px rgba(15,23,42,0.45)",
                opacity: showQuickAddFab ? 1 : 0,
                transform: showQuickAddFab ? "translateY(0)" : "translateY(16px)",
                pointerEvents: showQuickAddFab ? "auto" : "none",
                transition: "opacity 180ms ease, transform 180ms ease",
              }}
            >
              <AddIcon sx={{ mr: { xs: 0, md: 0.8 } }} />
              {!isMobile && "Add Expense"}
            </Fab>
          </Box>
        </Fade>
      </Container>
    </PageContainer>
  );
};

export default ExpensePage;
